import type { QueueRankConfig } from "@rivaled/shared";
import { Crown, Swords } from "lucide-react";

import { cn } from "../../lib/utils";
import { PlayerCard } from "../../match-flow/components/PlayerCard";
import { TeamHeader } from "../../match-flow/components/TeamHeader";
import type { PartyColor } from "../../match-flow/lib/party-colors";
import { getTeamColor } from "../../match-flow/lib/team-colors";
import type { MatchPlayer } from "./matchHelpers";
import { toFlowPlayer } from "./matchHelpers";
import { TeamCard, VsDivider } from "./TeamCard";

type TeamRostersProps = {
  players: MatchPlayer[];
  currentUserId: string;
  teamNames: string[];
  myTeam?: number;
  teamCount: number;
  isFFA: boolean;
  isVsLayout: boolean;
  sortedTeams: [number, MatchPlayer[]][];
  vsTeams: [number, MatchPlayer[]][];
  teamAvgElo: (teamNum: number) => number;
  rankConfig: QueueRankConfig | null;
  totalPlayers: number;
  getPartyColor: (partyId: string | null) => PartyColor | null;
};

export function TeamRosters({
  players,
  currentUserId,
  teamNames,
  myTeam,
  teamCount,
  isFFA,
  isVsLayout,
  sortedTeams,
  vsTeams,
  teamAvgElo,
  rankConfig,
  totalPlayers,
  getPartyColor: getPartyColorFn,
}: TeamRostersProps) {
  if (isFFA) {
    return (
      <div className="relative rounded-xl border border-surface-700/40 bg-surface-800/40 overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-500/20 to-transparent" />
        <div className="flex items-center gap-2 px-4 pt-3.5 pb-2">
          <Crown className="h-4 w-4 text-brand-400" />
          <h3 className="text-sm font-display font-bold uppercase tracking-wider text-text-primary">
            Competitors
          </h3>
          <span className="text-[11px] text-surface-500 font-display ml-auto">
            {totalPlayers} players
          </span>
        </div>
        <div className="p-2 space-y-0.5">
          {players.map((player, idx) => (
            <div key={player.userId} className="flex items-center gap-0">
              <span
                className={cn(
                  "w-7 text-center text-[11px] font-display font-bold tabular-nums shrink-0",
                  player.userId === currentUserId ? "text-brand-400" : "text-surface-600",
                )}
              >
                {idx + 1}
              </span>
              <div className="flex-1 min-w-0">
                <PlayerCard
                  player={toFlowPlayer(player)}
                  variant="compact"
                  isMe={player.userId === currentUserId}
                  rankConfig={rankConfig}
                  partyColor={getPartyColorFn(player.partyId)}
                  gameIdentity={player.gameIdentity}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (isVsLayout) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-0 items-stretch">
        <TeamCard
          teamNum={vsTeams[0][0]}
          players={vsTeams[0][1]}
          teamName={teamNames[vsTeams[0][0]] ?? `Team ${vsTeams[0][0]}`}
          isMyTeam={vsTeams[0][0] === myTeam}
          avgElo={teamAvgElo(vsTeams[0][0])}
          currentUserId={currentUserId}
          rankConfig={rankConfig}
          getPartyColor={getPartyColorFn}
        />

        <VsDivider />

        <TeamCard
          teamNum={vsTeams[1][0]}
          players={vsTeams[1][1]}
          teamName={teamNames[vsTeams[1][0]] ?? `Team ${vsTeams[1][0]}`}
          isMyTeam={vsTeams[1][0] === myTeam}
          avgElo={teamAvgElo(vsTeams[1][0])}
          currentUserId={currentUserId}
          rankConfig={rankConfig}
          getPartyColor={getPartyColorFn}
        />
      </div>
    );
  }

  // 3+ teams: battle royale-style bracket with team indicators
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 px-1">
        <Swords className="h-4 w-4 text-brand-400" />
        <span className="text-sm font-display font-bold uppercase tracking-wider text-text-primary">
          {teamCount} Teams
        </span>
        <div className="flex-1 h-px bg-gradient-to-r from-surface-700/40 to-transparent" />
      </div>

      <div
        className={cn(
          "grid gap-2",
          teamCount === 3 ? "grid-cols-1 sm:grid-cols-3" : "grid-cols-1 sm:grid-cols-2",
        )}
      >
        {sortedTeams.map(([teamNum, teamPlayers]) => {
          const isOwn = teamNum === myTeam;
          return (
            <div
              key={teamNum}
              className={cn(
                "relative rounded-xl border overflow-hidden transition-colors",
                isOwn
                  ? "bg-surface-800/60 border-brand-500/25"
                  : "bg-surface-800/40 border-surface-700/40",
              )}
            >
              <div
                className={cn(
                  "absolute top-0 left-0 right-0 h-0.5",
                  getTeamColor(teamNum, myTeam).accent,
                )}
              />

              <TeamHeader
                teamName={teamNames[teamNum] ?? `Team ${teamNum}`}
                isMyTeam={isOwn}
                rightContent={
                  <span className="text-[11px] font-display font-semibold text-surface-500 tabular-nums">
                    AVG {teamAvgElo(teamNum)}
                  </span>
                }
              />
              <div className="p-2 space-y-0.5">
                {teamPlayers.map((player) => (
                  <PlayerCard
                    key={player.userId}
                    player={toFlowPlayer(player)}
                    variant="compact"
                    isMe={player.userId === currentUserId}
                    rankConfig={rankConfig}
                    partyColor={getPartyColorFn(player.partyId)}
                    gameIdentity={player.gameIdentity}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
